<template>
  <v-card min-height="800">
    <v-card-text>
      <!-- File input -->
      <v-row>
        <v-col cols="5">
          <v-file-input
            dense
            accept=".csv"
            :label="$t('modal.csvSearch.label.upload')"
            v-model="csvFile"
            v-on:click:clear="onClearSelected"
          />
        </v-col>
        <v-col>
          <v-btn
            small
            :disabled="!csvFile"
            v-on:click="onFileSelected"
            color="green"
            :loading="csvTableLoading"
          >
            Read CSV
            <v-icon right dark>mdi-cloud-upload</v-icon>
          </v-btn>
        </v-col>
      </v-row>

      <!-- Column mapping -->
      <v-row dense>
        <v-col>
          <v-expansion-panels v-model="openedPanel">
            <v-expansion-panel>
              <v-expansion-panel-header class="font-weight-black">
                {{ $t("modal.csvSearch.label.csvOptions") }}
              </v-expansion-panel-header>
              <v-expansion-panel-content>
                  <strong>Field Name Mapping</strong>
                  <v-row>
                    <v-col>
                      <v-row>
                        <v-col>
                          <code>name</code> <span class="caption">- Required</span>
                          <br />
                          <code>localId</code> <span class="caption">- Your internal ID</span>
                          <br />
                          <code>group</code> <span class="caption">- Group Name</span>
                          <br />
                          <code>type</code> <span class="caption">- Person, Organization or Vessel</span>
                          <br />
                          <code>dob</code> <span class="caption">- YYYY-MM-DD</span>
                        </v-col>
                        <v-col>
                          <code>address1</code>, <code>address2</code>
                          <br />
                          <code>city</code>, <code>stateOrProvince</code>
                          <br />
                          <code>postalCode</code>
                          <br />
                          <code>country</code>
                          <br />
                          <code>citizenship</code>
                          <br />
                        </v-col>
                        <v-col>
                          <code>vatNumber</code>, <code>vatCountryCode</code>
                          <br>
                          <code>licenseNumber</code>, <code>licenseCountry</code>
                          <br>
                          <code>ein</code>
                        </v-col>
                      </v-row>
                    </v-col>
                    <v-col cols="3">
                      <v-select
                        :items="delimeters"
                        :label="$t('label.delimiter')"
                        v-model="delimiter"
                        dense
                      ></v-select>
                    </v-col>
                  </v-row>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-col>
      </v-row>

      <!-- Table -->
      <v-row>
        <v-col>
          <v-data-table
            :loading="csvTableLoading"
            :headers="headers"
            :items="cases"
            :no-data-text="tableText"
            dense
            :items-per-page="10"
          >
            <template v-slot:item.group="{ item }">
              <span v-if="item.caseGroupInvalid" class="red--text"
                >Invalid Group</span
              >
              <span v-else-if="item.group">{{ item.group }}</span>
            </template>

            <template v-slot:item.dob="{ item }">
              <span v-if="item.dobInvalid" class="red--text"
                >Invalid Format</span
              >
              <span v-else-if="item.dob">{{ item.dob }}</span>
            </template>

            <template v-slot:item.type="{ item }">
              <span v-if="item.caseTypeInvalid" class="red--text"
                >Invalid Type</span
              >
              <span v-else-if="item.type">{{ item.type }}</span>
            </template>

            <template v-slot:item.vat="{ item }">
              <span>{{ item.vatNumber }}</span>
              <span v-if="item.vatCountryCode"> - {{ item.vatCountryCode }}</span>
            </template>

            <template v-slot:item.addressIncluded="{ item }">
              <v-icon v-if="item.addressIncluded" small>mdi-check</v-icon>
            </template>

            <template v-slot:item.actions="prop">
              <v-btn
                text
                x-small
                color="red"
                @click="onDeleteClick(prop.item.name)"
              >
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        text
        @click="onCancel"
        :disabled="buttonsDisabled"
        >{{ $t("label.cancel") }}</v-btn
      >
      <v-btn
        color="primary"
        @click="onConfirm"
        :disabled="cases.length < 1 || buttonsDisabled"
      >
        {{ $t("label.save") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import Papa from "papaparse";
import { isValidDate } from "../utils/dateUtils";
import { getCaseGroupId } from "../utils/caseGroupUtils";

export default {
  name: "CaseCSVForm",

  data() {
    return {
      csvFile: null,
      cases: [],
      openedPanel: [0],

      // table
      csvTableLoading: false,
      tableText: "Upload CSV cases",
      headers: [
        { text: "ID", value: "localId" },
        { text: "Search Name", value: "name" },
        { text: "Type", value: "type" },
        { text: "Date of Birth", value: "dob" },
        { text: "Citizenship", value: "citizenship" },
        //{ text: "SSN", value: "ssn" },
        { text: "EIN", value: "ein" },
        { text: "VAT", value: "vat" },
        { text: "Address", value: "addressIncluded" },
        { text: "Group", value: "group" },
        { text: "Actions", value: "actions", sortable: false, align: "right" },
      ],

      // column labels
      delimiter: ",",
      delimeters: [
        { text: "Comma ( , )", value: "," },
        { text: "Pipe ( | ) ", value: "|" },
      ],
    };
  },
  props: {
    buttonsDisabled: {
      type: Boolean,
      default: false,
    },
    entityTypes: {
      type: Array,
      required: true
    }

  },
  methods: {
    isValidDate,
    getCaseGroupId,
    onCancel() {
      this.$emit("onCancel");
      this.csvFile = null;
      this.tableText = "Upload CSV cases";
      this.cases = [];
    },
    onClearSelected() {
      this.tableText = "Upload CSV cases";
      this.cases = [];
    },
    onFileSelected() {
      if (!this.csvFile) {
        return false;
      }

      this.csvTableLoading = true;
      this.openedPanel = []; // Close file options panel

      this.csvFileName = this.csvFile.name;
      try {
        Papa.parse(this.csvFile, {
          header: true,
          skipEmptyLines: true,
          delimiter: this.delimiter,
          complete: function (result) {
            if (result.errors.length > 0) {
              console.log(result.errors);
              this.tableText =
                "Error uploading CSV data, please check your delimiter and data headers.";
              return;
            }

            // create new array with using name header

            var formattedArray = result.data.reduce((filtered, record) => {
              var newCase = {};

              if (!record["name"]) {
                return filtered;
              }
              newCase.name = record["name"];

              if (record["dob"]) {
                if (!this.isValidDate(record["dob"])) {
                  newCase.dobInvalid = true;
                } else {
                  newCase.dob = record["dob"];
                }
              }

              if (record["citizenship"]) {
                newCase.citizenship = record["citizenship"];
              }

              // if (record[this.ssnColumnName]) {
              //   newCase.ssn = record["ssn"];
              // }

              if (record["localId"]) {
                newCase.localId = record["localId"];
              }

              if (record["ein"]) {
                newCase.ein = record["ein"];
              }

              if (record["vatNumber"]) {
                newCase.vatNumber = record["vatNumber"];
              }

              if (record["vatCountryCode"]) {
                newCase.vatCountryCode = record["vatCountryCode"];
              }

              /**
               * Drivers license must at least include the license number
               */
              if (record["licenseNumber"]) {
                var driversLicense = {};
                driversLicense.licenseNumber =
                  record["licenseNumber"];
                if (record["licenseCountry"]) {
                  driversLicense.licenseCountry =
                    record["licenseCountry"];
                }
                newCase.driversLicense = driversLicense;
              }

              // Case group
              if (record["group"]) {
                record["group"] = record["group"].trim();
                // if able to set case group ID, else make a warning
                let caseGroupId = this.getCaseGroupId(record["group"]);
                if (caseGroupId) {
                  newCase.group = record["group"];
                  newCase.caseGroupId = caseGroupId;
                } else {
                  newCase.caseGroupInvalid = true;
                }
              }

              // Type
              if (record["type"]) {
                var index = this.entityTypes.indexOf(record["type"].toLowerCase());
                if (index >= 0) {
                  newCase.type = this.entityTypes[index];
                } else {
                  newCase.caseTypeInvalid = true;
                }
              }

              // Address
              if (
                record["address1"] ||
                record["address2"] ||
                // record["address3"] ||
                // record["address4"] ||
                record["city"] ||
                record["stateOrProvince"] ||
                record["postalCode"] ||
                record["country"]
              ) {
                var address = {
                  address1: record["address1"],
                  address2: record["address2"],
                  city: record["city"],
                  stateOrProvince: record["stateOrProvince"],
                  postalCode: record["postalCode"],
                  country: record["country"],
                };
                newCase.address = address;
                newCase.addressIncluded = true;
              }

              filtered.push(newCase);
              return filtered;
            }, []);

            if (formattedArray.length < 1) {
              this.tableText =
                "Unable to parse tabular data, please check your delimiter and data headers.";
              return;
            }

            // remove duplicates
            // const seen = new Set();
            // const filteredArray = formattedArray.filter((el) => {
            //   const duplicate = seen.has(el.name);
            //   seen.add(el.name);
            //   return !duplicate;
            // });

            this.cases = formattedArray;
          }.bind(this),
        });
      } catch (e) {
        console.error("CSV import error", e);
      }

      this.csvTableLoading = false;
    },
    onConfirm() {
      this.$emit("onConfirmed", this.cases);
    },
    onDeleteClick(name) {
      this.cases = this.cases.filter(function (obj) {
        return obj.name !== name;
      });
    },
  },
};
</script>